import React from 'react';
import { Document, Page, View, Text } from '@react-pdf/renderer';
import { commonStyles, COLORS, formatExamDate, historyToString, calculateVolume, formatListItems, buildCombinedReason, getFacilityDisplayName } from '../utils/pdfHelpers';
import { PdfPageHeader, PdfPatientBox, PdfCallReport, PdfAccentSectionHeader, PdfSectionHeader, PdfLabelValue, PdfMeasurementBox, PdfCommentsBox, PdfFooter } from '../utils/PdfSharedComponents';

export function PediatricHipsPDF({ data, facilityInfo }) {
  // history is an object for this form, handle separately
  const parts = [];
  if (data.reason && String(data.reason).trim()) parts.push(String(data.reason).trim());
  const hist = data.history || {};
  if (hist.breechPresentation) parts.push('Breech presentation');
  if (hist.prematurity) parts.push('Prematurity');
  if (hist.familyHistoryDDH) parts.push('Family history of DDH');
  const reason = parts.length === 0 ? 'Unspecified' : parts.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join('. ');

  const rh = data.rightHip || {};
  const lh = data.leftHip || {};

  const isAlphaAbnormal = (angle) => angle && parseFloat(angle) < 60;

  const measurementsText = [
    (rh.evaluated && rh.alphaAngle) ? `Right hip alpha angle: ${rh.alphaAngle}°` : null,
    (lh.evaluated && lh.alphaAngle) ? `Left hip alpha angle: ${lh.alphaAngle}°` : null,
  ].filter(Boolean).join('\n');

  return (
    <Document>
      <Page size="LETTER" style={commonStyles.page}>
        <PdfPageHeader facilityInfo={facilityInfo} examTitle="PEDIATRIC HIPS ULTRASOUND WORKSHEET" />
        <PdfPatientBox
          firstName={data.firstName}
          lastName={data.lastName}
          mrn={data.mrn}
          examDate={data.examDate}
          technologist={data.technologist}
          studentWorksheet={data.studentWorksheet}
          supervisingSonographer={data.supervisingSonographer}
          reason={reason}
        />
        <PdfCallReport show={data.callReport} phoneNumber={data.phoneNumber} />

        <View style={commonStyles.twoCol}>
          <View style={commonStyles.colLeft}>
            <PdfMeasurementBox title="MEASUREMENTS">
              <Text style={commonStyles.infoBoxText}>{measurementsText || 'None recorded'}</Text>
            </PdfMeasurementBox>

            <View style={commonStyles.measurementBox}>
              <Text style={[commonStyles.infoBoxText, { fontFamily: 'Helvetica-Bold', marginBottom: 4 }]}>RISK FACTORS</Text>
              <Text style={commonStyles.infoBoxText}>Breech presentation: {hist.breechPresentation ? 'Yes' : 'No'}</Text>
              <Text style={commonStyles.infoBoxText}>Prematurity: {hist.prematurity ? 'Yes' : 'No'}</Text>
              <Text style={commonStyles.infoBoxText}>Family history of DDH: {hist.familyHistoryDDH ? 'Yes' : 'No'}</Text>
            </View>

            <PdfCommentsBox comments={data.comments} />
          </View>

          <View style={commonStyles.colRight}>
            {rh.evaluated && (
              <>
                <PdfAccentSectionHeader title="RIGHT HIP" />
                <PdfLabelValue label="Status:" value={rh.status || ''} isAbnormal={rh.status && rh.status !== 'Normal'} />
                {rh.alphaAngle && (
                  <PdfLabelValue
                    label="Alpha angle:"
                    value={`${rh.alphaAngle}°`}
                    isAbnormal={isAlphaAbnormal(rh.alphaAngle)}
                  />
                )}
                <PdfLabelValue label="Instability:" value={rh.instability ? 'Present' : 'None'} isAbnormal={rh.instability} />
                <PdfLabelValue label="Joint effusion:" value={rh.jointEffusion ? 'Present' : 'None'} isAbnormal={rh.jointEffusion} />
              </>
            )}

            {lh.evaluated && (
              <>
                <PdfAccentSectionHeader title="LEFT HIP" />
                <PdfLabelValue label="Status:" value={lh.status || ''} isAbnormal={lh.status && lh.status !== 'Normal'} />
                {lh.alphaAngle && (
                  <PdfLabelValue
                    label="Alpha angle:"
                    value={`${lh.alphaAngle}°`}
                    isAbnormal={isAlphaAbnormal(lh.alphaAngle)}
                  />
                )}
                <PdfLabelValue label="Instability:" value={lh.instability ? 'Present' : 'None'} isAbnormal={lh.instability} />
                <PdfLabelValue label="Joint effusion:" value={lh.jointEffusion ? 'Present' : 'None'} isAbnormal={lh.jointEffusion} />
              </>
            )}
          </View>
        </View>

        <PdfFooter facilityInfo={facilityInfo} />
      </Page>
    </Document>
  );
}
